import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { GridOptions, GridApi, ColDef } from 'ag-grid-community';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { AgGridReact } from 'ag-grid-react';
import { delay } from '@mummssoftware/utils/utils';
import { isSmallTouchDevice } from '@mummssoftware/utils/web';
import { AgGridTableWrapper } from '@mummssoftware/common-ui';
import { MessageOverlay } from './MessageOverlay';
import { VisitContext } from '../Context/contexts/VisitContext';
import { getDateColumns } from './utils';
import {
  FrequencyCellRenderer,
  NoteCountCellRenderer,
  DoneCellRenderer,
  DoneActionCellRenderer,
  RefusedActionCellRenderer,
} from './CellRenderer';
import {
  noteDef,
  taskDef,
  frequencyDef,
  doneDef,
  refusedCellDef,
} from './ColumnDef';
import {
  TaskPlanningDiv,
  TaskPlanningGridScroll,
  TasksWrapper,
} from '../../components/layout/VisitWrapper';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

const frameworkComponents = {
  frequencyCellRenderer: FrequencyCellRenderer,
  noteCountCellRenderer: NoteCountCellRenderer,
  doneCellRenderer: DoneCellRenderer,
  doneActionCellRenderer: DoneActionCellRenderer,
  refusedActionCellRenderer: RefusedActionCellRenderer,
  customLoadingOverlay: MessageOverlay,
};

export const TaskPlanning = () => {
  const { initSavedVisit, userTasks, showOverlay } = useContext(VisitContext);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const isNarrowPhoneView = useMediaQuery('(max-width:600px)', {
    noSsr: true,
  });
  const isIPadLikeTouch = isSmallTouchDevice();
  const isNarrowOrIPad = isNarrowPhoneView || isIPadLikeTouch;

  // startDate in UTC format
  const startDate = initSavedVisit?.startDate as string;
  const dateColumnDefs = useMemo(() => getDateColumns(startDate), [startDate]);

  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const columnDefs: ColDef[] = useMemo(() => {
    // ag-grid only pins a contiguous block from the left, so the note + Task
    // columns are pinned together on narrow viewports; Task then stays visible
    // while date columns scroll.
    const leftPinned: Pick<ColDef, 'pinned'> = { pinned: 'left' };
    return [
      { ...noteDef, ...(isNarrowOrIPad ? leftPinned : {}) },
      { ...taskDef, ...(isNarrowOrIPad ? leftPinned : {}) },
      frequencyDef,
      ...dateColumnDefs,
      doneDef,
      refusedCellDef,
    ] as ColDef[];
  }, [dateColumnDefs, isNarrowOrIPad]);

  const onGridReady = useCallback(({ api }: any) => {
    setGridApi(api);
  }, []);

  const gridOptions: GridOptions = useMemo(
    () => ({
      defaultColDef: {
        resizable: true,
      },
      // Pinned + scrollable body needs horizontal scroll on the grid viewport; wide layout unchanged.
      suppressHorizontalScroll: !isNarrowOrIPad,
      columnDefs,
      onGridReady,
    }),
    [columnDefs, isNarrowOrIPad, onGridReady],
  );
  /**
   * overlay for saving and deleting API calls
   */
  const showTableOverlay = useCallback(async () => {
    await delay(300);
    gridApi && gridApi.showLoadingOverlay();
  }, [gridApi]);
  const hideTableOverlay = useCallback(async () => {
    await delay(300);
    gridApi && gridApi.hideOverlay();
  }, [gridApi]);

  useEffect(() => {
    if (!gridApi) return;
    if (showOverlay) {
      showTableOverlay();
    } else {
      hideTableOverlay();
    }
  }, [showOverlay, gridApi, showTableOverlay, hideTableOverlay]);

  return (
    <TasksWrapper id="task-wrapper">
      <TaskPlanningDiv id="task-planning">
        <TaskPlanningGridScroll id="task-planning-scroll">
          <AgGridTableWrapper
            id="task-table-wrapper"
            className={`${gridTheme.className} compact`}
          >
            <AgGridReact
              gridOptions={gridOptions}
              rowData={userTasks || []}
              rowHeight={gridTheme.rowHeight}
              headerHeight={gridTheme.headerHeight}
              frameworkComponents={frameworkComponents}
              loadingOverlayComponent="customLoadingOverlay"
              domLayout="autoHeight"
            />
          </AgGridTableWrapper>
        </TaskPlanningGridScroll>
      </TaskPlanningDiv>
    </TasksWrapper>
  );
};
